import React, { useEffect, useState } from 'react';
import { Button, Divider } from 'antd';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction'; // for selectable
import { DateTime, Interval } from 'luxon';
import { t } from 'i18next';
import { formatDate } from '@fullcalendar/core/index.js';
import { TimeslotModal } from 'components/TimeslotModal';
import serviceTypes from 'lib/fulfillmentTypes';
import { DEFAULT_STORE } from 'lib/constants';

const DUTCH_LOCALE = { locale: 'nl-NL' };
const LIMIT_CALENDAR = { days: 15 };
const weekdayMap = {
  ma: 1,
  di: 2,
  wo: 3,
  do: 4,
  vr: 5,
  za: 6,
  zo: 7
};

function firstAvailableServiceDate(
  serviceDays,
  today = DateTime.local(DUTCH_LOCALE)
) {
  const todayWeekday = today.weekday; // 1 (Mon) - 7 (Sun)

  // get service days sorted by weekday number
  const serviceWeekdays = Object.keys(serviceDays)
    .map((d) => weekdayMap[d])
    .sort((a, b) => a - b);

  // find first service day after today
  let nextWeekday = serviceWeekdays.find((day) => day > todayWeekday);

  // wrap around if none found (next week)
  if (!nextWeekday) {
    nextWeekday = serviceWeekdays[0];
  }

  // calculate the date difference
  const daysToAdd = (nextWeekday - todayWeekday + 7) % 7 || 7;

  return today.plus({ days: daysToAdd });
}

export function Calendar({
  postcodes,
  serviceSettings,
  fulfillmentType,
  postalCode,
  onDatetimeChange,
  value,
  store,
  pickupLocation,
  hideNextButton = false,
  fulfillmentLabel
}) {
  const [showModal, setShowModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState();
  const [timeslots, setTimeslots] = useState();
  const [timeslot, setTimeslot] = useState();
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState();
  const [selectedDate, setSelectedDate] = useState();
  const isDefaultStore = store?.name?.includes(DEFAULT_STORE);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  useEffect(() => {
    let selectedDateValue = null;
    let selectedTimeslot = null;
    const isDelivery = fulfillmentType === serviceTypes.DELIVERY;

    if (value) {
      // Split the string by "/"
      const [dateString, time] = value.split(' ');
      const [day, month, year] = dateString.split('/');

      // Create a new Date object (Note: months are zero-indexed in JavaScript)
      selectedDateValue = new Date(year, month - 1, day);
      selectedTimeslot = time;
    }

    setTimeslot(selectedTimeslot);
    setSelectedDate(selectedDateValue);
    if (isDelivery) setSelectedDeliveryDate(selectedDateValue);
  }, [fulfillmentType, value, store]);

  function attachSourcesForPickup() {
    const today = DateTime.local(DUTCH_LOCALE);
    const int = Interval.after(today, LIMIT_CALENDAR);
    const subInt = int.splitBy({ days: 1 });
    const events = [];
    const serviceDays = serviceSettings[pickupLocation].service_days;

    for (const dayInt of subInt) {
      const dayStartInt = dayInt.start;

      // exclude default store to check for beyond closing times for pickup
      // Orders can't be placed after closing hours for tomorrow
      const beyondClosingTimes =
        !isDefaultStore &&
        dayStartInt.diff(today, 'days').days == 1 && // tomorrow
        today.hour >= serviceSettings.common.closing_hour;

      for (const availableDay of Object.keys(store.opening_times)) {
        if (
          dayStartInt &&
          dayStartInt.weekdayShort === availableDay &&
          !beyondClosingTimes && // should be before the clsoing hours of the day
          !serviceSettings.common.excluded_dates.pickup.includes(
            dayStartInt.toISODate()
          )
        ) {
          // make sure to have enough time to drop-off the bike at the
          // request store, here we disable the first available opening day

          let enabled = true;
          let firstAvailableServiceDay = firstAvailableServiceDate(
            serviceDays,
            today
          );
          let firstAvailableServiceDayAfterToday = firstAvailableServiceDate(
            serviceDays,
            today.plus({ days: 1 })
          );
          let firstOpenDay = firstAvailableServiceDate(
            store.opening_times,
            firstAvailableServiceDayAfterToday
          );

          // disable if service day is tomorrow and hour is beyond closing hours
          if (
            dayStartInt.diff(firstAvailableServiceDay, 'days').days < 1 ||
            (dayStartInt.diff(firstOpenDay, 'day').days < 0 &&
              today.hour >= serviceSettings.common.closing_hour)
          ) {
            enabled = false;
          }

          // if it's the same day and beyond closing hours, disable pickup
          if (
            dayStartInt.hasSame(today, 'day') &&
            today.hour >= serviceSettings.common.closing_hour
          ) {
            continue;
          }

          if (!enabled && !isDefaultStore) continue;

          // disable store excluded days
          if ((store?.excluded_dates || []).includes(dayStartInt.toISODate())) {
            continue;
          }

          events.push({
            start: dayStartInt.toISODate(),
            // see https://fullcalendar.io/docs/background-events
            display: 'background',
            extendedProps: {
              region: store.name,
              timeslot: store.opening_times[availableDay]
            }
          });
        }
      }
    }
    return {
      id: 'regionBased',
      defaultAllDay: true,
      // className: 'bg-[#A8D9A5]!',
      events
    };
  }

  function attachSourcesForDelivery() {
    // retrieve the match with only numbers
    const matched = postalCode.trim().match(/(\d{4}).*/i);
    const [_, zipcode] = matched || ['', ''];
    const events = [];
    let selectedRegion = null;
    for (const region of Object.keys(postcodes)) {
      if (
        zipcode &&
        postcodes[region].map((x) => x.toString()).includes(zipcode)
      ) {
        selectedRegion = region;
        break;
      }
    }

    if (!selectedRegion) return;

    const today = DateTime.local(DUTCH_LOCALE);
    const int = Interval.after(today, LIMIT_CALENDAR);
    const subInt = int.splitBy({ days: 1 });
    // console.log('today', today.weekdayShort, 'int', subInt[0].start.weekdayShort);

    for (const dayInt of subInt) {
      const dayStartInt = dayInt.start;

      // Check if customer is ordering a day before and if the hour has
      // passed the closing time
      const beyondClosingTimes =
        dayStartInt.day - today.day == 1 &&
        today.hour >= serviceSettings.common.closing_hour;

      // console.log('dayStartInt ', dayStartInt.weekdayShort);

      for (const availableDay of Object.keys(
        serviceSettings[selectedRegion].service_days
      )) {
        const startFrom = serviceSettings[selectedRegion].opening_on;
        if (
          !dayStartInt.hasSame(today, 'day') &&
          dayStartInt.weekdayShort === availableDay &&
          !serviceSettings[selectedRegion]?.excluded_dates?.includes(
            dayStartInt.toISODate()
          ) &&
          !serviceSettings.common.excluded_dates.delivery.includes(
            dayStartInt.toISODate()
          ) &&
          !beyondClosingTimes &&
          // check if we have any new regions starting from a particular date,
          // if so, make sure only dates from then on are enabled
          (!startFrom ||
            (startFrom &&
              new Date(startFrom) <= new Date(dayStartInt.toISODate())))
        ) {
          events.push({
            start: dayStartInt.toISODate(),
            // see https://fullcalendar.io/docs/background-events
            display: 'background',
            extendedProps: {
              region: selectedRegion,
              timeslots:
                serviceSettings[selectedRegion].service_days[availableDay]
            }
          });
        }
      }
    }

    return {
      id: 'regionBased',
      defaultAllDay: true,
      // className: 'bg-[#A8D9A5]!',
      events
    };
  }

  function pickupOrDelivery() {
    switch (fulfillmentType) {
      case serviceTypes.PICKUP:
        return attachSourcesForPickup();
      case serviceTypes.DELIVERY:
        return attachSourcesForDelivery();
      default:
        return 'no events';
    }
  }

  function getFormattedDateValue(date) {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // @todo - this smells a bit weird
  function onChange() {
    if (selectedDate && timeslot) {
      onDatetimeChange(getFormattedDateValue(selectedDate) + ' ' + timeslot);
    }
  }

  return (
    <>
      <Divider plain className="mt-0!">
        <span className="text-gray-500">
          {t('steps.deliveryMoment.calendar.title')}
        </span>
      </Divider>
      <div className="mt-5" translate="no">
        <FullCalendar
          timeZone="Europe/Amsterdam"
          locales={[{ code: 'nl' }, { code: 'en' }]}
          headerToolbar={{
            left: 'title',
            center: '',
            right: ''
          }}
          nowIndicator={true}
          hiddenDays={[0]}
          plugins={[dayGridPlugin, interactionPlugin]}
          firstDay={1}
          initialDate={new Date()}
          dayHeaders={true}
          dayHeaderFormat={{ weekday: 'short' }}
          titleFormat={{ year: 'numeric', month: 'short' }}
          initialView="dayGridFourWeeks"
          contentHeight="auto"
          views={{
            dayGridFourWeeks: {
              type: 'dayGridMonth',
              duration: { weeks: 5 },
              validRange: function (currentDate) {
                const endDate = new Date();
                endDate.setDate(endDate.getDate() + 4 * 7);

                return { start: currentDate, end: endDate };
              },
              // see https://fullcalendar.io/docs/fixedWeekCount
              fixedWeekCount: false
            }
          }}
          eventClassNames={(arg) => {
            const { start, end } = arg.event._instance.range;

            const tz = (d) =>
              new Date(d.getTime() + d.getTimezoneOffset() * 60000);

            const isInBetween =
              selectedDate >= tz(start) && selectedDate < tz(end)
                ? 'event-chosen'
                : '';

            return `hover:cursor-pointer hover:bg-[green]! region region-${arg.event.extendedProps.region.toLowerCase()} ${isInBetween}`;
          }}
          dayCellDidMount={(arg) => {
            arg.el.classList.add('cell');
            const dayTopHref = arg.el.querySelector('.fc-daygrid-day-top > a');
            dayTopHref.classList.add('cell-link');
            dayTopHref.classList.add('hover:text-inherit');
            dayTopHref.classList.add('hover:cursor-default');
            dayTopHref.style.zIndex = 0;

            return arg;
          }}
          dayCellContent={(arg) => {
            return arg.date.getDate();
          }}
          eventClick={(info) => {
            info.jsEvent.preventDefault(); // don't let the browser navigate
            setSelectedRegion(null);
            const previouslyChosenEvents =
              document.querySelectorAll('.event-chosen');
            previouslyChosenEvents.forEach((prevItem) => {
              prevItem.classList.remove('event-chosen');
            });
            info.el.classList.add('event-chosen', 'text-[green]!');

            if (fulfillmentType == serviceTypes.DELIVERY) {
              setSelectedDeliveryDate(info.event.start);
              setTimeslots(info.event.extendedProps.timeslots);
              setSelectedRegion(info.event.extendedProps.region);
              openModal();
            } else if (fulfillmentType == serviceTypes.PICKUP) {
              setTimeslot(info.event.extendedProps.timeslot); // day is index + 1, we need only index
              setSelectedDate(info.event.start);
              if (hideNextButton) {
                onDatetimeChange(
                  getFormattedDateValue(info.event.start) +
                    ' ' +
                    info.event.extendedProps.timeslot
                );
              }
            }
          }}
          eventSources={[pickupOrDelivery()]}
        />
        {selectedDate && timeslot && (
          <div>
            <p className="my-3">
              {t('steps.deliveryMoment.calendar.selectedMoment.prefix')}
              {fulfillmentLabel
                ? fulfillmentLabel
                : selectedRegion
                  ? t('steps.deliveryMoment.fulfillmentType.delivery.title')
                  : t('steps.deliveryMoment.fulfillmentType.pickup.title')}
              <span> in </span>
              <span className="text-[#15803d] font-bold">
                {selectedRegion ? selectedRegion : store?.name}
              </span>
              {t('steps.deliveryMoment.calendar.selectedMoment.middle')}
              <span className="text-[#15803d] font-bold">
                {formatDate(selectedDate, {
                  weekday: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  month: 'long',
                  locale: t('type')
                })}
              </span>
              {t('steps.deliveryMoment.calendar.selectedMoment.suffix')}
              <span className="text-[#15803d] font-bold">{timeslot}</span>
            </p>
            {!hideNextButton && (
              <Button
                size="large"
                className="w-full"
                type="primary"
                onClick={() => onChange()}>
                {t('steps.deliveryMoment.calendar.button')}
              </Button>
            )}
          </div>
        )}
        <TimeslotModal
          showModal={showModal}
          closeModal={closeModal}
          selectedDate={selectedDeliveryDate}
          onSelectedDateChange={setSelectedDate}
          onTimeslotChange={(tslot) => {
            setTimeslot(tslot);
            if (hideNextButton) {
              onDatetimeChange(
                getFormattedDateValue(selectedDeliveryDate) + ' ' + tslot
              );
            }
          }}
          timeslots={timeslots}
        />
      </div>
    </>
  );
}
